import { createFileRoute } from '@tanstack/react-router';
import { useHasPermission } from '@/hooks/usePermissions';
import { useAssignableNumbers, useCallFlow } from '@/lib/voice/hooks';
import { sameDid } from '@/lib/voice/resolve-routing';
import { CallFlowBuilder } from '@/components/organisms/settings-call/call-flows/CallFlowBuilder';
import {
  IvrBuilderShell,
  IvrBuilderLoading,
  IvrBuilderNotFound,
} from '@/components/organisms/settings-call/ivr-builder-shell';

export const Route = createFileRoute('/_dashboard/dashboard/settings/call-settings/flows/$id')({
  component: CallFlowEditPage,
});

/**
 * Reads the ONE flow rather than finding it in the list.
 *
 * The list is a summary and a flow is a graph — publishing one rewrites the
 * numbers on its siblings, so a cached list entry can be a version behind in a
 * way that matters here. The detail query is the source of truth for the builder.
 *
 * Read-only viewers still get in: `company:view` opens the builder with every
 * control disabled, which is more use than a permission wall when someone is
 * trying to understand how a number is answered.
 */
function CallFlowEditPage() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const canEdit = useHasPermission('company:edit');
  const query = useCallFlow(id);
  const numbers = useAssignableNumbers();

  // Back to the number this flow answers, where it was most likely opened
  // from; a flow on no number (or several) goes back to the overview.
  const back = () => {
    const on = (numbers.data ?? []).filter((n) =>
      (query.data?.phoneNumbers ?? []).some((p) => sameDid(p, n.phoneNumber)),
    );
    if (on.length === 1) {
      return navigate({
        to: '/dashboard/settings/call-settings',
        search: { tab: 'numbers', number: on[0]!._id },
      });
    }
    return navigate({ to: '/dashboard/settings/call-settings', search: { tab: 'flows' } });
  };
  const openFlow = (flowId: string) =>
    navigate({
      to: '/dashboard/settings/call-settings/flows/$id',
      params: { id: flowId },
      search: { tab: 'flows' },
    });

  if (query.isLoading) return <IvrBuilderLoading onBack={back} />;
  if (!query.data) return <IvrBuilderNotFound onBack={back} />;

  return (
    <IvrBuilderShell>
      {/* Keyed by flow: switching flows from the header starts a fresh draft
          and undo history rather than carrying the last flow's over. */}
      <CallFlowBuilder
        key={id}
        flow={query.data}
        canEdit={canEdit}
        onBack={back}
        onOpenFlow={openFlow}
      />
    </IvrBuilderShell>
  );
}
